import { Position } from '../../../shared/types/protocol.ts';
import { Protocol } from '../../types/sharedTypeImpl.ts';
import { Change } from './change.ts';
import { getFieldChange, getSimpleChange } from './getChange.ts';


export function getPositionChanges(protocolNew: Protocol, protocolOld: Protocol): Change[] {
    const changes: Change[] = [];
    changes.push(...getAddedRemovedPositions(protocolNew, protocolOld));
    changes.push(...getModifiedPositions(protocolNew, protocolOld));
    return changes;
}

function getAddedRemovedPositions(protocolNew: Protocol, protocolOld: Protocol): Change[] {
    const changes: Change[] = [];

    const oldPositions = protocolOld.positions || [];
    const newPositions = protocolNew.positions || [];

    const addedPositions = subtractPositions(newPositions, oldPositions);
    const removedPositions = subtractPositions(oldPositions, newPositions);

    for (const position of addedPositions) {
        changes.push({
            path: getPath(position),
            changeDescription: 'Added',
            changeType: 'add',
        });
    }
    for (const position of removedPositions) {
        changes.push({
            path: getPath(position),
            changeDescription: 'Removed',
            changeType: 'remove',
        });
    }
    return changes;
}

function getModifiedPositions(protocolNew: Protocol, protocolOld: Protocol): Change[] {
    const changes: (Change | null)[] = [];

    const oldPositions = new Map(protocolOld.positions.map((pos) => [pos.positionNumber, pos]));
    for (const newPosition of protocolNew.positions) {
        const oldPosition = oldPositions.get(newPosition.positionNumber);
        if (!oldPosition || newPosition.type !== oldPosition.type) continue;// Skipped added/removed positions

        changes.push(getFieldChange(newPosition, oldPosition, 'nominal', getPath(newPosition, 'Nominal')));
        changes.push(getFieldChange(newPosition, oldPosition, 'symbol', getPath(newPosition, 'Symbol')));
        changes.push(getBooleanChange(newPosition, oldPosition, 'isCritical', getPath(newPosition, 'Critical')));
        changes.push(getFieldChange(newPosition, oldPosition, 'upperTolerance', getPath(newPosition, 'Upper tolerance')));
        changes.push(getFieldChange(newPosition, oldPosition, 'lowerTolerance', getPath(newPosition, 'Lower tolerance')));
        changes.push(getBooleanChange(newPosition, oldPosition, 'useGeneralTolerance', getPath(newPosition, 'Use GT')));
        changes.push(getBooleanChange(newPosition, oldPosition, 'isChamfer', getPath(newPosition, 'Chamfer')));
        changes.push(getFieldChange(newPosition, oldPosition, 'toolType', getPath(newPosition, 'Tool')));
        changes.push(...getDescriptionChange(newPosition, oldPosition));
        changes.push(getMarkersPositionsChange(newPosition, oldPosition));
        changes.push(getImageChange(newPosition, oldPosition));
    }
    return changes.filter((change) => change !== null) as Change[];
}


function getDescriptionChange(newPosition: Position, oldPosition: Position): (Change | null)[] {
    const changes: (Change | null)[] = [];

    const path = getPath(newPosition, 'Description');
    changes.push(getFieldChange(newPosition, oldPosition, 'description', path));


    const oldMarkerDescriptions = oldPosition.markers?.map(marker => marker.text)
        .filter(text => text).sort() || [];
    const newMarkerDescriptions = newPosition.markers?.map(marker => marker.text)
        .filter(text => text).sort() || [];
    for (let i = 0; i < Math.max(oldMarkerDescriptions.length, newMarkerDescriptions.length); i++) {
        changes.push(getSimpleChange(newMarkerDescriptions[i], oldMarkerDescriptions[i], path));
    }

    return changes;
}

function getMarkersPositionsChange(newPosition: Position, oldPosition: Position): Change | null {
    const newMarkers = newPosition.markers || [];
    const oldMarkers = oldPosition.markers || [];

    const markerCountChanged = newMarkers.length !== oldMarkers.length;

    let markerPositionChanged = false;
    for (let i = 0; i < Math.min(newMarkers.length, oldMarkers.length); i++) {
        const newMarker = newMarkers[i];
        const oldMarker = oldMarkers[i];

        if (newMarker.x !== oldMarker.x ||
            newMarker.y !== oldMarker.y ||
            newMarker.x2 !== oldMarker.x2 ||
            newMarker.y2 !== oldMarker.y2 ||
            newMarker.sizeFactor !== oldMarker.sizeFactor
        ) {
            markerPositionChanged = true;
            break;
        }
    }

    if (markerCountChanged || markerPositionChanged) {
        return {
            path: getPath(newPosition, 'Markers'),
            changeDescription: 'Changed',
            changeType: 'modify',
        };
    } else {
        return null;
    }
}

function getImageChange(newPosition: Position, oldPosition: Position): Change | null {
    const path = getPath(newPosition, 'Image');
    if (newPosition.imageFileName && !oldPosition.imageFileName) {
        return {
            path,
            changeDescription: 'Added',
            changeType: 'add',
        };
    } else if (newPosition.imageFileName !== oldPosition.imageFileName) {
        return {
            path,
            changeDescription: 'Changed',
            changeType: 'modify',
        };
    }
    return null;
}


function getBooleanChange(
    newPosition: Position,
    oldPosition: Position,
    field: keyof Position,
    path: string[],
): Change | null {
    const newValue = newPosition[field] ?? false;
    const oldValue = oldPosition[field] ?? false;
    return getSimpleChange(newValue, oldValue, path);
}

function subtractPositions(positions1: Position[], positions2: Position[]): Position[] {
    const positions = positions1.filter((position1) => !positions2.some(position2 =>
        position1.positionNumber === position2.positionNumber && position1.type === position2.type
    ));
    return positions;
}

function getPath(position: Position, field?: string): string[] {
    return [
        '#' + position.positionNumber,
        ...(field ? [field] : []),
    ];
}
